// looks for [data-combobox]. works in conjunction with the combobox tag helper.
import htmx from 'htmx.org';
import { isEmpty } from '../utilities';
import { ComboboxElement } from '../interfaces';

const liCss = 'border-b-1 border-gray-lighter p-2 cursor-pointer hover:outline-primary-bright hover:outline-4 hover:-outline-offset-2 focus:outline-primary-bright focus:outline-4 focus:-outline-offset-2 aria-selected:bg-primary aria-selected:text-white';
const cboxElements = new Map<string, ComboboxElement>();
const cboxLists = new Map<string, HTMLLIElement[]>();

export function bindCombobox(content?: Element | Document | null | undefined) {
    if (!content) {
        content = document;
    }
    content.querySelectorAll<HTMLElement>('[data-combobox]').forEach(cbox => {
        if (isEmpty(cbox.id)) {
            return;
        }
        const id = cbox.id;        
        const select = document.querySelector<HTMLSelectElement>(`select[name=${id}]`);
        const input = document.querySelector<HTMLInputElement>(`#${id}`);
        const toggle = document.querySelector<HTMLButtonElement>(`#${id}-toggle`);
        const clear = document.querySelector<HTMLButtonElement>(`#${id}-clear`);
        const list = document.querySelector<HTMLUListElement>(`#${id}-list`);

        if (!select || !input || !toggle || !clear || !list) {
            return;
        }

        cboxElements.set(id, {
            select,
            input,
            toggle,
            clear,
            list
        });

        input.value = select.selectedIndex > 0 ? select.options[select.selectedIndex].text : '';
        clear.hidden = select.selectedIndex < 1;
        input.addEventListener('keyup', () => inputKeyup(id));
        toggle.addEventListener('click', () => toggleClicked(id));
        clear.addEventListener('click', () => clearClicked(id));
        document.addEventListener("click", e => cboxBlur(e, id));
        document.addEventListener("keyup", e => cboxBlur(e, id));
    });
}

function inputKeyup(id: string) {
    const input = cboxElements.get(id)?.input;
    const clear = cboxElements.get(id)?.clear;
    const select = cboxElements.get(id)?.select;
    if (!input || !clear || !select) {
        return;
    }

    clear.hidden = select.selectedIndex < 1 || input.value !== select.options[select.selectedIndex].text;

    buildList(id);
    showList(id);
}

function toggleClicked(id: string) {
    const list = cboxElements.get(id)?.list;
    if (!list) {
        return;
    }
    
    if (list.hidden) {
        if (list.children.length === 0) {
            buildList(id);
        }
        showList(id);
    }
    else {
        hideList(id);
    }
    cboxElements.get(id)?.input?.focus();
}

function clearClicked(id: string) {
    const input = cboxElements.get(id)?.input;
    const clear = cboxElements.get(id)?.clear;
    const list = cboxElements.get(id)?.list;
    const select = cboxElements.get(id)?.select;
    if (!input || !clear || !list || !select) {
        return;
    }

    input.value = '';
    clear.hidden = true;
    list.innerHTML = '';
    select.selectedIndex = 0;
    hideList(id);
    input.focus();
}

function liKeyup(evt: KeyboardEvent, li: HTMLLIElement, id: string) {
    evt.preventDefault();
    if (evt.code === 'Space' || evt.code === 'Enter') {
        liSelected(li, id);
    }
    else if (evt.code === 'ArrowUp') {
        const list = cboxLists.get(id);
        if (!list) {
            return;
        }
        const selectedIndex = list.indexOf(li) || 0;
        if (selectedIndex < 1) {
            cboxElements.get(id)?.input?.focus();
            hideList(id);
        }
        else {
            list[selectedIndex - 1].focus();
        }
    }
    else if (evt.code === 'ArrowDown') {
        const list = cboxLists.get(id);
        if (!list) {
            return;
        }
        const selectedIndex = cboxLists.get(id)?.indexOf(li) ?? 0;
        if (selectedIndex > -1 && selectedIndex < list.length) {
            list[selectedIndex + 1].focus();
        }
    }
}

function liSelected(li: HTMLLIElement, id: string) {
    const input = cboxElements.get(id)?.input;
    const clear = cboxElements.get(id)?.clear;
    const select = cboxElements.get(id)?.select;
    if (!input || !clear || !select || select.options[0].value === li.getAttribute('data-value')) {
        return;
    }

    input.value = li.innerText;
    clear.hidden = false;
    cboxElements.get(id)?.list?.querySelectorAll<HTMLLIElement>('[aria-selected=true]').forEach(li => li.ariaSelected = 'false');
    li.ariaSelected = 'true';
    select.value = li.getAttribute('data-value') || '';
    input.focus();
    hideList(id);
}

function cboxBlur(evt: Event, id: string) {
    const input = cboxElements.get(id)?.input;
    const list = cboxLists.get(id);
    const select = cboxElements.get(id)?.select;
    const clear = cboxElements.get(id)?.clear;
    const toggle = cboxElements.get(id)?.toggle;
    if (!input || !list || !select || !clear || !toggle) {
        return;
    }
    const active = evt.target;
    if (active === input || active === toggle || active === clear) {
        return;
    }

    let blurred = true;

    for (let i = 0; i < list.length; i++) {
        if (active === list[i]) {
            blurred = false;
            break;
        }
    }

    if (blurred) {
        hideList(id);
        input.value = select.selectedIndex < 1 ? '' : select.options[select.selectedIndex].text;
        clear.hidden = select.selectedIndex < 1;
    }
}

function buildList(id: string) {
    const ul = cboxElements.get(id)?.list;
    if (!ul) {
        return;
    }

    const list: HTMLLIElement[] = [];
    const text = cboxElements.get(id)?.input?.value.toLowerCase() || '';
    const isTextEmpty = isEmpty(text);
    let count = 0;

    ul.innerHTML = '';
    cboxElements.get(id)?.select?.querySelectorAll<HTMLOptionElement>('option').forEach(option => {
        if (option.value !== '' && (isTextEmpty || option.text.toLowerCase().includes(text))) {
            const li = document.createElement('li');
            li.id = `${id}-list-${count}`;
            li.className = liCss;
            li.tabIndex = count === 0 ? 0 : -1;
            li.role = 'option';
            li.ariaPosInSet = `${count + 1}`;
            li.ariaSelected = 'false';
            li.innerText = option.text;
            li.setAttribute('data-value', option.value);
            li.addEventListener('click', () => liSelected(li, id));
            li.addEventListener('keyup', evt => liKeyup(evt, li, id));
            li.addEventListener('blur', evt => cboxBlur(evt, id));
            ul.appendChild(li);
            list.push(li);
            ++count;
        }
    });
    if (count === 0) {
        ul.innerHTML = `<li class="p-2 cursor-not-allowed">No results found</li>`;
    }
    else {
        for (let i = 0; i < ul.childElementCount; i++) {
            ul.children[i].ariaSetSize = `${count}`;
        }
    }
    cboxLists.set(id, list);
}

function showList(id: string) {
    const list = cboxElements.get(id)?.list;
    if (!list) {
        return;
    }
    
    cboxElements.get(id)?.input?.setAttribute('aria-expanded', 'true');
    list.hidden = false;
}

function hideList(id: string) {
    const list = cboxElements.get(id)?.list;
    if (!list) {
        return;
    }

    cboxElements.get(id)?.input?.setAttribute('aria-expanded', 'false');
    list.hidden = true;
}

document.addEventListener('DOMContentLoaded', () => bindCombobox(document));

htmx.on('htmx:afterSwap', (e) => {
    bindCombobox(e.target as Element);
});
